import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getData } from '../../../webService/webservice.js';
import { useSubscriptionRenewal } from '../../../context/SubscriptionRenewalContext.jsx';
import PackageInvoiceModal from '../../../components/Subscription/PackageInvoiceModal.jsx';
import { Icon } from '@iconify/react';

const PaymentVerifyPage = () => {
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('order_id');
    const navigate = useNavigate();
    const [status, setStatus] = useState('VERIFYING'); // VERIFYING, SUCCESS, FAILED
    const [message, setMessage] = useState('Verifying your payment...');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const { fetchCurrentSubscription } = useSubscriptionRenewal();

    useEffect(() => {
        if (!orderId) {
            setStatus('FAILED');
            setMessage('Order ID is missing.');
            return;
        }

        const verifyOrder = async () => {
            try {
                const token = localStorage.getItem("Token");
                const res = await getData(`payments/verify/${orderId}`, {}, token);

                if (res?.success) {
                    if (res.paymentStatus === 'SUCCESS') {
                        setStatus('SUCCESS');
                        setMessage('Payment successful! Your subscription has been renewed.');
                        fetchCurrentSubscription();

                        // Try loading generated invoice for orderId
                        try {
                            const invRes = await getData(`package-invoices/subscription/${orderId}`, {}, token);
                            if (invRes?.data) {
                                setInvoiceData(invRes.data);
                            }
                        } catch (invErr) {
                            console.log("Could not load invoice directly by orderId", invErr);
                        }

                    } else if (res.paymentStatus === 'FAILED') {
                        setStatus('FAILED');
                        setMessage(res.message || 'Payment failed. Please try again.');
                    } else {
                        setStatus('FAILED');
                        setMessage('Payment is pending or unverified. Please contact support if amount was deducted.');
                    }
                } else {
                    setStatus('FAILED');
                    setMessage('Failed to verify payment status.');
                }
            } catch (error) {
                setStatus('FAILED');
                setMessage('Error occurred while verifying payment.');
            }
        };

        verifyOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const handleContinue = () => {
        navigate('/dashboard');
    };

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100vh', backgroundColor: '#f9fafb'
        }}>
            <div style={{
                maxWidth: '440px', width: '100%', backgroundColor: '#fff',
                padding: '40px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                textAlign: 'center'
            }}>
                {status === 'VERIFYING' && (
                    <div>
                        <h2 style={{ color: '#374151', marginBottom: '16px' }}>Verifying Payment...</h2>
                        <div className="spinner" style={{
                            border: '4px solid rgba(0,0,0,0.1)', borderLeftColor: '#3b82f6',
                            borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite',
                            margin: '0 auto'
                        }}></div>
                    </div>
                )}

                {status === 'SUCCESS' && (
                    <div>
                        <div style={{ color: '#10b981', marginBottom: '16px' }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Payment Successful!</h2>
                        <p style={{ color: '#4b5563', marginBottom: '24px' }}>{message}</p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button onClick={() => setShowInvoiceModal(true)} style={{
                                backgroundColor: '#3c0008', color: '#fff', padding: '12px 24px', border: 'none',
                                borderRadius: '6px', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                            }}>
                                <Icon icon="solar:printer-bold-duotone" style={{ fontSize: '20px' }} />
                                Print Package Invoice
                            </button>

                            <button onClick={handleContinue} style={{
                                backgroundColor: '#10b981', color: '#fff', padding: '12px 24px', border: 'none',
                                borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', width: '100%'
                            }}>
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                )}

                {status === 'FAILED' && (
                    <div>
                        <div style={{ color: '#dc2626', marginBottom: '16px' }}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                            </svg>
                        </div>
                        <h2 style={{ color: '#111827', marginBottom: '16px' }}>Payment Failed</h2>
                        <p style={{ color: '#4b5563', marginBottom: '24px' }}>{message}</p>
                        <button onClick={() => navigate(-1)} style={{
                            backgroundColor: '#dc2626', color: '#fff', padding: '12px 24px', border: 'none',
                            borderRadius: '6px', fontSize: '16px', cursor: 'pointer', width: '100%'
                        }}>
                            Go Back
                        </button>
                    </div>
                )}
            </div>

            <PackageInvoiceModal
                isOpen={showInvoiceModal}
                onClose={() => setShowInvoiceModal(false)}
                invoiceData={invoiceData}
                subscriptionId={orderId}
            />

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default PaymentVerifyPage;

